import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Activity, Client, ActivityType, DanceRoles, Enrollment } from '../../../pure-js/types/GrooverTypes'
import {
  CourseRegistrationFormValues,
  getValidationSchema,
  getValidationSchemaPartner,
  getValidationSchemaSsn,
  getValidationSchemaPartnerSsn
} from '../helpers/CourseRegistrationStepOneHelper'
import { State } from '../hooks/useAppState'
import { useClient } from '../hooks/useClient'
import { useCurrency } from '../hooks/useCurrency'
import { useForm } from '../hooks/useForm'
import usePartnerAndRoles from '../hooks/usePartnerAndRoles'
import { useCheckUnfinishedEnrollment } from '../hooks/useCheckUnfinishedEnrollment'
import { getInternationalPhoneNumber, getCountryCodeFromClient } from '../../../pure-js/libs/PhoneHelper'
import CourseRegistrationStepOneClosedContent from './CourseRegistrationStepOneClosedContent'
import CourseRegistrationStepOneOpenedContent from './CourseRegistrationStepOneOpenedContent'
import ResumeRegistrationDialog from './ResumeRegistrationDialog'
import { ClosedContainer } from './StyledComponents'
import { DiscountPricingConfig } from '../../../pure-js/types/DiscountTypes'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { prefetchObfuscatedEnrollmentSafely } from '../libs/ReactQueryHooks'

type Props = {
  state: State
  activity: Activity
  onNextStep: () => void
  onOpenStepOne: () => void
  registerActiveStep: number
  onSetFormErrors: (hasFormErrors: boolean) => void
  onSetIsPartnerSenior: (isPartnerSenior: boolean) => void
  onSetIsPartnerStudent: (isPartnerStudent: boolean) => void
  onSetIsPartnerSibling: (isPartnerSibling: boolean) => void
  onSetIsMainUserSenior: (isMainUserSenior: boolean) => void
  onSetIsMainUserStudent: (isMainUserStudent: boolean) => void
  onSetIsMainUserSibling: (isMainUserSibling: boolean) => void
  onSaveRegistrationData: (data: CourseRegistrationFormValues) => void
  onSetDiscountType: (discountType: DiscountPricingConfig['registrationType']) => void
  onSetRole: (role: DanceRoles | null) => void
  onSetPartnerRole: (partnerRole: DanceRoles | null) => void
  onSetDiscountRegion: (region: string) => void
}

const CourseRegistrationStepOne = ({
  state,
  activity,
  onNextStep,
  onOpenStepOne,
  registerActiveStep,
  onSetFormErrors,
  onSetIsPartnerSenior,
  onSetIsPartnerStudent,
  onSetIsPartnerSibling,
  onSetIsMainUserSenior,
  onSetIsMainUserStudent,
  onSetIsMainUserSibling,
  onSaveRegistrationData,
  onSetDiscountType,
  onSetRole,
  onSetPartnerRole,
  onSetDiscountRegion
}: Props) => {
  const { hasPartner, setHasPartner, role, partnerRole, changeRole, handleRemovePartner } = usePartnerAndRoles(
    onSetDiscountType,
    state.role,
    state.partnerRole,
    onSetRole,
    onSetPartnerRole
  )
  const client = useClient(activity)
  const currency = useCurrency(client)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { check: checkUnfinishedEnrollment, isLoading: isCheckingUnfinishedEnrollment } = useCheckUnfinishedEnrollment()

  const [resumeEnrollment, setResumeEnrollment] = useState<Enrollment | null>(null)
  const [pendingFormData, setPendingFormData] = useState<CourseRegistrationFormValues | null>(null)

  const [mainUserPhoneCountry, setMainUserPhoneCountry] = useState<string | undefined>(undefined)
  const [partnerPhoneCountry, setPartnerPhoneCountry] = useState<string | undefined>(undefined)

  const mainUserPhoneCountryRef = useRef<string | undefined>(undefined)
  const partnerPhoneCountryRef = useRef<string | undefined>(undefined)
  mainUserPhoneCountryRef.current = mainUserPhoneCountry
  partnerPhoneCountryRef.current = partnerPhoneCountry

  const validationSchema = useMemo(
    () =>
      hasPartner
        ? client?.showSsnInRegistration
          ? getValidationSchemaPartnerSsn(currency, client, mainUserPhoneCountryRef, partnerPhoneCountryRef)
          : getValidationSchemaPartner(client, mainUserPhoneCountryRef, partnerPhoneCountryRef)
        : client?.showSsnInRegistration
          ? getValidationSchemaSsn(currency, client, mainUserPhoneCountryRef)
          : getValidationSchema(client, mainUserPhoneCountryRef),
    [hasPartner, client, currency, client?.showSsnInRegistration]
  )

  const forcePartnerSection =
    activity.registrationOverride?.mode === 'couple' ||
    (!state.individualFlow && client?.activityTypesSupported === ActivityType.Couples)

  const shouldExpandPartnerSectionFromStep0 =
    registerActiveStep === 1 && state.individualFlow === false && !activity.singleRegistration

  // Auto-set hasPartner to true when couple registration is forced/selected.
  useEffect(() => {
    if ((forcePartnerSection || shouldExpandPartnerSectionFromStep0) && !hasPartner) {
      setHasPartner(true)
    }
  }, [forcePartnerSection, shouldExpandPartnerSectionFromStep0, hasPartner, setHasPartner])

  const formProps = useForm<CourseRegistrationFormValues>(validationSchema, {
    defaultValues: {
      name: '',
      phoneNumber: '',
      email: '',
      role: DanceRoles.Leader,
      partnerName: '',
      partnerPhoneNumber: '',
      partnerEmail: '',
      partnerRole: DanceRoles.Follower,
      mainUserSsn: '',
      partnerUserSsn: '',
      mainUserAddress: '',
      partnerUserAddress: ''
    },
    mode: 'all',
    reValidateMode: 'onChange'
  }) as unknown as UseFormReturn<CourseRegistrationFormValues, any, CourseRegistrationFormValues>

  const handleMainUserPhoneCountryChange = (country: string) => {
    // Ref must be updated before trigger(): setState is async; Yup reads ref synchronously on trigger.
    mainUserPhoneCountryRef.current = country
    setMainUserPhoneCountry(country)
    formProps.trigger('phoneNumber')
  }
  const handlePartnerPhoneCountryChange = (country: string) => {
    partnerPhoneCountryRef.current = country
    setPartnerPhoneCountry(country)
    formProps.trigger('partnerPhoneNumber')
  }

  // When client loads after first paint, align phone country state/refs with client.country (same source as PhoneInput default).
  useEffect(() => {
    if (!client) return
    const cc = String(getCountryCodeFromClient(client))
    if (mainUserPhoneCountry === undefined) {
      mainUserPhoneCountryRef.current = cc
      setMainUserPhoneCountry(cc)
      if (formProps.getValues('phoneNumber')) {
        void formProps.trigger('phoneNumber')
      }
    }
    if (hasPartner && partnerPhoneCountry === undefined) {
      partnerPhoneCountryRef.current = cc
      setPartnerPhoneCountry(cc)
      if (formProps.getValues('partnerPhoneNumber')) {
        void formProps.trigger('partnerPhoneNumber')
      }
    }
  }, [client, hasPartner, mainUserPhoneCountry, partnerPhoneCountry, formProps.trigger])

  useEffect(() => {
    if (Object.keys(formProps.formState.errors).length > 0) {
      onSetFormErrors(true)
    } else {
      onSetFormErrors(false)
    }
  }, [Object.keys(formProps.formState.errors).length])

  // Warm the cache while the resume dialog is visible so "Continue my registration" feels instant.
  useEffect(() => {
    if (!resumeEnrollment?.id) return
    void prefetchObfuscatedEnrollmentSafely(queryClient, resumeEnrollment.id)
  }, [resumeEnrollment?.id, queryClient])

  const proceedToNextStep = useCallback(
    (normalizedData: CourseRegistrationFormValues) => {
      onSaveRegistrationData(normalizedData)
      onNextStep()
    },
    [onSaveRegistrationData, onNextStep]
  )

  const onSubmit = async (data: CourseRegistrationFormValues) => {
    // Role is selected in different steps depending on flow. Validate before proceeding.
    const isSingleRegistration = activity.singleRegistration || false
    const isRoleSelectionInStep1 = !state.individualFlow && !isSingleRegistration
    const resolvedMainUserRole = state.obfuscatedEnrollment?.mainUser?.type || role
    const resolvedPartnerRole = state.obfuscatedEnrollment?.partnerUser?.type || partnerRole

    if (isRoleSelectionInStep1 && !resolvedMainUserRole) {
      formProps.setError('role', {
        type: 'required',
        message: getGrooverText('registrationValidationsErrorRoleRequired')
      })
      return
    }

    if (hasPartner && !isSingleRegistration && !resolvedPartnerRole) {
      formProps.setError('partnerRole', {
        type: 'required',
        message: getGrooverText('registrationValidationsErrorRoleRequired')
      })
      return
    }

    const defaultCountry = getCountryCodeFromClient(client)
    const mainCountry = mainUserPhoneCountry || defaultCountry
    const partnerCountry = partnerPhoneCountry || defaultCountry
    const normalizedData: CourseRegistrationFormValues = {
      ...data,
      role,
      partnerRole,
      phoneNumber: data.phoneNumber
        ? getInternationalPhoneNumber(data.phoneNumber, mainCountry as any)
        : data.phoneNumber,
      partnerPhoneNumber: data.partnerPhoneNumber
        ? getInternationalPhoneNumber(data.partnerPhoneNumber, partnerCountry as any)
        : data.partnerPhoneNumber
    }

    if (client?.id && normalizedData.phoneNumber) {
      const unfinished = await checkUnfinishedEnrollment({
        clientId: client.id,
        activityId: activity.id,
        phoneNumber: normalizedData.phoneNumber,
        partnerPhoneNumber: normalizedData.partnerPhoneNumber || undefined
      })
      if (unfinished) {
        setPendingFormData(normalizedData)
        setResumeEnrollment(unfinished)
        return
      }
    }

    proceedToNextStep(normalizedData)
  }

  const handleResume = useCallback(async () => {
    if (!resumeEnrollment) return
    const enrollmentId = resumeEnrollment.id
    // Await in case the user clicks before the dialog-open prefetch finishes; never block navigation on failure.
    await prefetchObfuscatedEnrollmentSafely(queryClient, enrollmentId)
    setResumeEnrollment(null)
    setPendingFormData(null)
    navigate(buildRoute(RoutePath.REGISTER_OBFUSCATED, { activityId: activity.id, enrollmentId }))
  }, [resumeEnrollment, activity.id, navigate, queryClient])

  const handleStartNew = useCallback(() => {
    const data = pendingFormData
    setResumeEnrollment(null)
    setPendingFormData(null)
    if (data) proceedToNextStep(data)
  }, [pendingFormData, proceedToNextStep])

  return (
    <>
      {registerActiveStep !== 1 ? (
        <ClosedContainer onClick={registerActiveStep > 1 ? onOpenStepOne : undefined}>
          <CourseRegistrationStepOneClosedContent state={state} />
        </ClosedContainer>
      ) : (
        <CourseRegistrationStepOneOpenedContent
          state={state}
          client={client as Client}
          activity={activity}
          formProps={formProps}
          role={role}
          hasPartner={hasPartner}
          onNextStep={onNextStep}
          setHasPartner={setHasPartner}
          changeRole={changeRole}
          partnerRole={partnerRole}
          onSubmit={onSubmit}
          onSetIsMainUserStudent={onSetIsMainUserStudent}
          onSetIsMainUserSibling={onSetIsMainUserSibling}
          onSetIsMainUserSenior={onSetIsMainUserSenior}
          onSetIsPartnerStudent={onSetIsPartnerStudent}
          onSetIsPartnerSibling={onSetIsPartnerSibling}
          onSetIsPartnerSenior={onSetIsPartnerSenior}
          onSetDiscountType={onSetDiscountType}
          onSetDiscountRegion={onSetDiscountRegion}
          handleRemovePartner={handleRemovePartner}
          disableRemovePartner={forcePartnerSection}
          onMainUserPhoneCountryChange={handleMainUserPhoneCountryChange}
          onPartnerPhoneCountryChange={handlePartnerPhoneCountryChange}
          isCheckingUnfinishedEnrollment={isCheckingUnfinishedEnrollment}
        />
      )}
      {resumeEnrollment && (
        <ResumeRegistrationDialog
          enrollment={resumeEnrollment}
          onResume={handleResume}
          onStartNew={handleStartNew}
          isOpen={!!resumeEnrollment}
        />
      )}
    </>
  )
}

export default memo(CourseRegistrationStepOne)
